// modals/quotations/ViewQuotation.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    Printer, 
    Download, 
    X,
    Clock,
    CheckCircle,
    XCircle,
    AlertTriangle,
    FileText,
  
    DollarSign
} from 'react-feather';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { quotationActions } from '../../action/quotationActions';

const ViewQuotation = ({ quotation, onClose }) => {
    const [quotationDetails, setQuotationDetails] = useState(quotation);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (quotation && !quotation.items) {
            loadQuotationDetails();
        } else {
            setItems(quotation.items || []);
            setQuotationDetails(quotation);
        }
    }, [quotation]);

    const loadQuotationDetails = async () => {
        try {
            setLoading(true);
            const result = await quotationActions.getQuotation(quotation.id);
            
            if (result.success) {
                setQuotationDetails(result.data.quotation || result.data);
                setItems(result.data.items || []);
            }
        } catch (error) {
            console.error('Error loading quotation details:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    const formatDateTime = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY hh:mm A') : 'N/A';
    };

    const getStatusBadge = () => {
        const badges = {
            'draft': { class: 'badge bg-secondary', icon: <FileText size={14} /> },
            'pending': { class: 'badge bg-warning', icon: <Clock size={14} /> },
            'approved': { class: 'badge bg-success', icon: <CheckCircle size={14} /> },
            'rejected': { class: 'badge bg-danger', icon: <XCircle size={14} /> },
            'expired': { class: 'badge bg-dark', icon: <AlertTriangle size={14} /> },
            'converted': { class: 'badge bg-info', icon: <DollarSign size={14} /> }
        };
        const badge = badges[quotationDetails?.status?.toLowerCase()] || { class: 'badge bg-secondary', icon: null };
        return (
            <span className={`badge ${badge.class}`}>
                {badge.icon} {quotationDetails?.status?.toUpperCase()}
            </span>
        );
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Quotation - ${quotationDetails.quotation_no}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        h1 { color: #333; }
                        .header { margin-bottom: 30px; }
                        .info { margin-bottom: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th { background-color: #4CAF50; color: white; padding: 10px; text-align: left; }
                        td { padding: 8px; border-bottom: 1px solid #ddd; }
                        tr:nth-child(even) { background-color: #f2f2f2; }
                        .text-end { text-align: right; }
                        .footer { margin-top: 30px; }
                    </style>
                </head>
                <body>
                    <h1>Quotation</h1>
                    <div class="header">
                        <p><strong>Quotation No:</strong> ${quotationDetails.quotation_no}</p>
                        <p><strong>Date:</strong> ${formatDate(quotationDetails.quotation_date)}</p>
                        <p><strong>Valid Till:</strong> ${formatDate(quotationDetails.expiry_date)}</p>
                    </div>
                    <div class="info">
                        <h3>Customer Information</h3>
                        <p><strong>Name:</strong> ${quotationDetails.customer_name || 'N/A'}</p>
                        <p><strong>Phone:</strong> ${quotationDetails.customer_phone || 'N/A'}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Product</th>
                                <th>Qty</th>
                                <th class="text-end">Rate</th>
                                <th class="text-end">Disc %</th>
                                <th class="text-end">Tax %</th>
                                <th class="text-end">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${items.map((item, index) => `
                                <tr>
                                    <td>${index + 1}</td>
                                    <td>${item.product_name}</td>
                                    <td>${item.quantity}</td>
                                    <td class="text-end">${formatCurrency(item.unit_price)}</td>
                                    <td class="text-end">${item.discount || 0}%</td>
                                    <td class="text-end">${item.tax_percent || 0}%</td>
                                    <td class="text-end">${formatCurrency(item.total_cost)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colspan="6" class="text-end">Subtotal:</th>
                                <th class="text-end">${formatCurrency(quotationDetails.subtotal)}</th>
                            </tr>
                            <tr>
                                <th colspan="6" class="text-end">Tax:</th>
                                <th class="text-end">${formatCurrency(quotationDetails.tax)}</th>
                            </tr>
                            <tr>
                                <th colspan="6" class="text-end">Discount:</th>
                                <th class="text-end">-${formatCurrency(quotationDetails.discount)}</th>
                            </tr>
                            <tr>
                                <th colspan="6" class="text-end">Shipping:</th>
                                <th class="text-end">${formatCurrency(quotationDetails.shipping)}</th>
                            </tr>
                            <tr>
                                <th colspan="6" class="text-end">Adjustment:</th>
                                <th class="text-end">${formatCurrency(quotationDetails.adjustment)}</th>
                            </tr>
                            <tr>
                                <th colspan="6" class="text-end fw-bold">Grand Total:</th>
                                <th class="text-end fw-bold">${formatCurrency(quotationDetails.grand_total)}</th>
                            </tr>
                        </tfoot>
                    </table>
                    <div class="footer">
                        <p><strong>Notes:</strong> ${quotationDetails.notes || 'N/A'}</p>
                        <p><strong>Terms & Conditions:</strong> ${quotationDetails.terms_conditions || 'N/A'}</p>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const handleDownload = async () => {
        try {
            Swal.fire('Info', 'Download feature coming soon', 'info');
        } catch (error) {
            console.error('Error downloading:', error);
        }
    };

    if (!quotationDetails) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Quotation Details - {quotationDetails.quotation_no}
                        </h5>
                        <div className="d-flex gap-2">
                            <button 
                                type="button" 
                                className="btn btn-outline-primary btn-sm"
                                onClick={handlePrint}
                                title="Print"
                            >
                                <Printer size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-outline-success btn-sm"
                                onClick={handleDownload}
                                title="Download"
                            >
                                <Download size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>
                    
                    <div className="modal-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading details...</p>
                            </div>
                        ) : (
                            <>
                                {/* Header Info */}
                                <div className="row mb-4">
                                    <div className="col-md-8">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Quotation No</p>
                                                        <h6 className="fw-bold">{quotationDetails.quotation_no}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Reference No</p>
                                                        <h6>{quotationDetails.reference_no || 'N/A'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Quotation Date</p>
                                                        <h6>{formatDate(quotationDetails.quotation_date)}</h6>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Valid Till</p>
                                                        <h6 className={dayjs(quotationDetails.expiry_date).isBefore(dayjs()) ? 'text-danger' : ''}>
                                                            {formatDate(quotationDetails.expiry_date)}
                                                        </h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Created By</p>
                                                        <h6>{quotationDetails.created_by || 'Admin'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Created At</p>
                                                        <h6>{formatDateTime(quotationDetails.created_at)}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <p className="mb-1 text-muted">Status</p>
                                                        {getStatusBadge(quotationDetails.status)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Customer Info */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">Customer Information</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Name</p>
                                                <p className="fw-bold">{quotationDetails.customer_name || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Phone</p>
                                                <p>{quotationDetails.customer_phone || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Items Table */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">Items</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-bordered mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Product Code</th>
                                                        <th>Product Name</th>
                                                        <th className="text-end">Qty</th>
                                                        <th className="text-end">Rate</th>
                                                        <th className="text-end">Disc %</th>
                                                        <th className="text-end">GST %</th>
                                                        <th className="text-end">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="8" className="text-center py-4">
                                                                No items found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        items.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td>{index + 1}</td>
                                                                <td>{item.product_code || item.sku || '-'}</td>
                                                                <td>
                                                                    <strong>{item.product_name}</strong>
                                                                </td>
                                                                <td className="text-end">{item.quantity}</td>
                                                                <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                                <td className="text-end">{item.discount || 0}%</td>
                                                                <td className="text-end">{item.tax_percent || 0}%</td>
                                                                <td className="text-end fw-bold">{formatCurrency(item.total_cost)}</td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <th colSpan="7" className="text-end">Subtotal:</th>
                                                        <th className="text-end">{formatCurrency(quotationDetails.subtotal)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="7" className="text-end">Tax:</th>
                                                        <th className="text-end">{formatCurrency(quotationDetails.tax)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="7" className="text-end">Additional Discount:</th>
                                                        <th className="text-end text-danger">-{formatCurrency(quotationDetails.discount)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="7" className="text-end">Shipping:</th>
                                                        <th className="text-end">{formatCurrency(quotationDetails.shipping)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="7" className="text-end">Adjustment:</th>
                                                        <th className="text-end">{formatCurrency(quotationDetails.adjustment)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="7" className="text-end fw-bold">Grand Total:</th>
                                                        <th className="text-end fw-bold text-primary">
                                                            {formatCurrency(quotationDetails.grand_total)}
                                                        </th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Notes and Terms */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Notes</h6>
                                            </div>
                                            <div className="card-body">
                                                <p className="mb-0">{quotationDetails.notes || 'No notes'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Terms & Conditions</h6>
                                            </div>
                                            <div className="card-body">
                                                <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                                                    {quotationDetails.terms_conditions || 'No terms specified'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <X size={14} className="me-2" />
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

ViewQuotation.propTypes = {
    quotation: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewQuotation;